import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, CheckCircle, ArrowRight, Trophy } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MockQuizPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const participantId = searchParams.get('participant_id');

  // Load mock quiz questions
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/competition/mock-quiz`);
        setQuestions(response.data.questions);
        setTimeRemaining(response.data.duration_seconds);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load mock quiz:', err);
        navigate('/lobby');
      }
    };
    loadQuestions();
  }, [navigate]);

  // Timer
  useEffect(() => {
    if (loading || isComplete) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, isComplete]);

  const handleComplete = useCallback(async () => {
    setIsComplete(true);
    
    // Notify backend
    try {
      await axios.post(`${API_URL}/api/competition/complete-mock-quiz`, {
        participant_id: participantId
      });
    } catch (err) {
      console.error('Failed to complete mock quiz:', err);
    }
  }, [participantId]);

  const handleAnswerSelect = (option) => {
    if (selectedAnswer) return; // Already answered
    
    setSelectedAnswer(option);
    const currentQuestion = questions[currentIndex];
    
    if (option === currentQuestion.correct_option) {
      setScore(prev => prev + 1);
    }

    // Auto-advance after short delay
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setSelectedAnswer(null);
      } else {
        handleComplete();
      }
    }, 500);
  };

  const handleReturnToLobby = () => {
    navigate(`/lobby?code=${sessionStorage.getItem('event_code') || ''}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center">
        <div className="text-center">
          <Clock className="w-16 h-16 text-[#2565eb] animate-pulse mx-auto mb-4" />
          <p className="text-[#64748b]">Loading mock quiz...</p>
        </div>
      </div>
    );
  }

  // Complete Screen
  if (isComplete) {
    const percentage = Math.round((score / questions.length) * 100);
    
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-fade-in-up">
          <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0] ${
            percentage >= 70 ? 'bg-[#22c55e] shadow-[#16a34a]' : 'bg-[#f59e0b] shadow-[#d97706]'
          }`}>
            <Trophy className="w-14 h-14 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Mock Quiz Complete!
          </h1>
          
          <p className="text-[#64748b] text-lg mb-6">
            You scored <span className="font-bold text-[#0f172a]">{score}</span> out of <span className="font-bold text-[#0f172a]">{questions.length}</span>
          </p>

          <div className="card-olympiad p-6 mb-8">
            <p className="text-sm text-[#64748b] mb-4">
              Great warm-up! You're now ready for the competition.
            </p>
            <Progress value={percentage} className="h-3" />
          </div>

          <button
            onClick={handleReturnToLobby}
            className="btn-secondary flex items-center justify-center gap-2 mx-auto"
            data-testid="return-to-lobby-button"
          >
            Return to Lobby
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      {/* Header with Timer */}
      <header className="p-4 bg-gradient-to-r from-[#face39] to-[#f59e0b]">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="text-[#0f172a]">
            <p className="text-sm font-medium">Mock Quiz</p>
            <p className="text-xs">Warm-up questions</p>
          </div>
          <div className="flex items-center gap-2 bg-white/90 px-4 py-2 rounded-full">
            <Clock className="w-5 h-5 text-[#d97706]" />
            <span className="text-xl font-bold text-[#0f172a] font-mono">
              {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
            </span>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="px-4 py-2 bg-white border-b border-[#e2e8f0]">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-sm text-[#64748b] mb-2">
            <span>Question {currentIndex + 1} of {questions.length}</span>
            <span>Score: {score}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Question */}
      <main className="flex-1 p-4 sm:p-6 max-w-3xl mx-auto w-full">
        <div className="card-olympiad p-6 sm:p-8 animate-fade-in-up">
          <h2 className="text-xl sm:text-2xl font-bold text-[#0f172a] mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {currentQuestion.question_text}
          </h2>

          <div className="space-y-3">
            {['A', 'B', 'C', 'D'].map((option) => {
              const optionKey = `option_${option.toLowerCase()}`;
              const optionText = currentQuestion[optionKey];
              const isSelected = selectedAnswer === option;
              const isCorrect = currentQuestion.correct_option === option;
              
              let buttonStyle = "w-full text-left rounded-2xl p-4 sm:p-5 font-semibold transition-all border-2";
              
              if (selectedAnswer) {
                if (isCorrect) {
                  buttonStyle += " bg-[#f0fdf4] border-[#22c55e] text-[#15803d]";
                } else if (isSelected) {
                  buttonStyle += " bg-[#fef2f2] border-[#ef4444] text-[#b91c1c]";
                } else {
                  buttonStyle += " bg-white border-[#e2e8f0] text-[#64748b]";
                }
              } else {
                buttonStyle += " bg-white border-[#e2e8f0] text-[#0f172a] hover:border-[#2565eb] hover:bg-[#eff6ff] cursor-pointer";
              }
              
              return (
                <button
                  key={option}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={selectedAnswer !== null}
                  className={buttonStyle}
                  data-testid={`mock-option-${option.toLowerCase()}`}
                >
                  <span className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center font-bold text-[#64748b]">
                      {option}
                    </span>
                    <span className="flex-1">{optionText}</span>
                    {selectedAnswer && isCorrect && (
                      <CheckCircle className="w-6 h-6 text-[#22c55e]" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MockQuizPage;
